import type { TFunction } from "i18next";
import * as yup from "yup";
import {
  createContext,
  useContext,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from "react";

export const aiPromptFieldName = "prompt";

// Mirrors the backend cap (AiChatDto @MaxLength(2000)).
export const AI_PROMPT_MAX_LENGTH = 2000;

/** The validation message is UI copy, so the schema is built per render. */
export const buildPromptInputSchema = (t: TFunction) =>
  yup.object().shape({
    [aiPromptFieldName]: yup
      .string()
      .max(AI_PROMPT_MAX_LENGTH, t("common:ai_assistant.prompt_too_long"))
      .required(),
  });

export type ChatBotFields = yup.InferType<
  ReturnType<typeof buildPromptInputSchema>
>;

export type ResponseMessageTypes = "initial" | "new_response" | "edit";

export interface IAIAssistantContext {
  onSelectChat: ((sessionId: string) => Promise<void>) | null;
  onSelectQuery:
    ((sessionId: string, messageId: number) => Promise<void>) | null;
  startNewChat: (() => void) | null;
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
  isTempChat: boolean;
  startTempChat: () => void;
  stopTempChat: () => void;
  responseMessageTypeRef: RefObject<ResponseMessageTypes>;
}

export const AIAssistantContext = createContext<
  IAIAssistantContext | undefined
>(undefined);

export const useAIAssistantContext = (): IAIAssistantContext => {
  const context = useContext(AIAssistantContext);
  if (!context) {
    throw new Error(
      "useAIAssistant must be used within an AIAssistantContext.Provider",
    );
  }
  return context;
};
